// ./src/utils/api/EmbeddingEventTemplateService.ts
import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { User } from "./UserService";

// GraphQL queries
const EMBEDDINGEVENTTEMPLATE_LIST_QUERY = gql`
  query EmbeddingEventTemplateList(
    $filter: EmbeddingEventTemplateFilter
    $params: PaginationAndSorting
  ) {
    embeddingEventTemplateList(filter: $filter, params: $params) {
      code
      data {
        id
        name
        description
        templateHtml
        layoutType
        previewImageUrl
        rights
        status
        createdAt
        updatedAt
        createdBy
        updatedBy
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EMBEDDINGEVENTTEMPLATE_GET_QUERY = gql`
  query EmbeddingEventTemplateGet($id: ID) {
    embeddingEventTemplateGet(id: $id) {
      code
      data {
        id
        name
        description
        templateHtml
        layoutType
        previewImageUrl
        rights
        status
        createdAt
        updatedAt
        createdBy
        updatedBy
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EMBEDDINGEVENTTEMPLATE_GET_MANY_QUERY = gql`
  query EmbeddingEventTemplateGetMany($ids: [ID]) {
    embeddingEventTemplateGetMany(ids: $ids) {
      code
      data {
        id
        name
        description
        templateHtml
        layoutType
        previewImageUrl
        rights
        status
        createdAt
        updatedAt
        createdBy
        updatedBy
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// GraphQL mutations
const EMBEDDINGEVENTTEMPLATE_CREATE_MUTATION = gql`
  mutation EmbeddingEventTemplateCreate($params: EmbeddingEventTemplateInput) {
    embeddingEventTemplateCreate(params: $params) {
      code
      data {
        id
        name
        description
        templateHtml
        layoutType
        previewImageUrl
        rights
        status
        createdAt
        updatedAt
        createdBy
        updatedBy
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EMBEDDINGEVENTTEMPLATE_UPDATE_MUTATION = gql`
  mutation EmbeddingEventTemplateUpdate(
    $where: EmbeddingEventTemplateWhereInput
    $params: EmbeddingEventTemplateInput
  ) {
    embeddingEventTemplateUpdate(where: $where, params: $params) {
      code
      data {
        id
        name
        description
        templateHtml
        layoutType
        previewImageUrl
        rights
        status
        createdAt
        updatedAt
        createdBy
        updatedBy
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EMBEDDINGEVENTTEMPLATE_REMOVE_MUTATION = gql`
  mutation EmbeddingEventTemplateRemove(
    $where: EmbeddingEventTemplateWhereInput
  ) {
    embeddingEventTemplateRemove(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const EMBEDDINGEVENTTEMPLATE_REMOVE_MANY_MUTATION = gql`
  mutation EmbeddingEventTemplateRemoveMany(
    $where: [EmbeddingEventTemplateWhereInput]
  ) {
    embeddingEventTemplateRemoveMany(where: $where) {
      code
      data {
        id
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

// Types
export interface EmbeddingEventTemplate {
  id?: string | undefined;
  name?: string | undefined;
  description?: string | undefined;
  templateHtml?: string | undefined;
  layoutType?: string | undefined;
  previewImageUrl?: string | undefined;
  status?: number | undefined;
  rights?: string[] | undefined;
  createdAt?: string | undefined;
  updatedAt?: string | undefined;
  createdBy?: string | undefined;
  updatedBy?: string | undefined;
  userCreated?: User;
  userUpdated?: User;
}

export interface EmbeddingEventTemplateInput {
  name?: string | null | undefined;
  description?: string | null | undefined;
  templateHtml?: string | null | undefined;
  layoutType?: string | null | undefined;
  previewImageUrl?: string | null | undefined;
  status?: number | null | undefined;
}

export interface EmbeddingEventTemplateFilter {
  id?: string[] | null | undefined;
  name?: string[] | null | undefined;
  layoutType?: string[] | null | undefined;
  searchKeyword?: string | null | undefined;
  status?: number[] | null | undefined;
}

export interface EmbeddingEventTemplateWhereInput {
  id?: string | undefined;
}

/**
 * EmbeddingEventTemplate service that handles all embedding event template-related API calls
 * Supports full CRUD operations: list, get, getMany, create, update, remove, removeMany
 */
export class EmbeddingEventTemplateService {
  /**
   * Get a list of embedding event templates with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: EmbeddingEventTemplateFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      EMBEDDINGEVENTTEMPLATE_LIST_QUERY,
      {
        filter: filter || {},
        params: {
          pagination: pagination || undefined,
          sortBy: sortBy || undefined,
        },
      }
    );
    return result;
  }

  /**
   * Get a single embedding event template by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      EMBEDDINGEVENTTEMPLATE_GET_QUERY,
      {
        id: id,
      }
    );
    return result;
  }

  /**
   * Get multiple embedding event templates by IDs (preserves order)
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(
      EMBEDDINGEVENTTEMPLATE_GET_MANY_QUERY,
      {
        ids: ids,
      }
    );
    return result;
  }

  /**
   * Create a new embedding event template
   */
  static async create(params: EmbeddingEventTemplateInput): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      EMBEDDINGEVENTTEMPLATE_CREATE_MUTATION,
      {
        params,
      }
    );
    return result;
  }

  /**
   * Update an existing embedding event template
   */
  static async update(
    where: EmbeddingEventTemplateWhereInput,
    params: EmbeddingEventTemplateInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      EMBEDDINGEVENTTEMPLATE_UPDATE_MUTATION,
      {
        where,
        params,
      }
    );
    return result;
  }

  /**
   * Remove/delete an embedding event template
   */
  static async remove(id: string): Promise<OpResult> {
    const result = await ApiService.executeMutation(
      EMBEDDINGEVENTTEMPLATE_REMOVE_MUTATION,
      {
        where: { id },
      }
    );
    return result;
  }

  /**
   * Remove/delete multiple embedding event templates
   */
  static async removeMany(ids: string[]): Promise<OpResult> {
    const where = ids.map((id) => ({ id }));
    const result = await ApiService.executeMutation(
      EMBEDDINGEVENTTEMPLATE_REMOVE_MANY_MUTATION,
      {
        where,
      }
    );
    return result;
  }
}
